import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { markRaw } from 'vue';
export default {
  name: 'ActuatorMonitor',
  data() {
    return {
      // 状态控制
      wellCoverOpen: false,
      isInWell: false,
      actuatorOpen: false,
      isViewingActuator: false,
      wellCover: null,
      wellBarrelHidden: false,
      // 井桶隐藏状态
      wellBarrelTransparent: true,
      // 井桶前壁透明状态
      actuatorRotating: false,
      // 执行器旋转状态
      actuatorRotationSpeed: 0.03,
      // 执行器旋转速度
      // 执行器指标数据
      actuatorMetrics: {
        temperature: 61012509004401,
        // 温度 (°C)
        location: '-',
        pressure: 5,
        // 压力 (MPa)
        vibration: 4,
        // 振动 (mm/s)
        current: 7,
        // 电流 (A)
        current1: 25,
        // 电流 (A)
        current2: 12.1,
        // 电流 (A)
        status: '正常' // 状态
      },
      // 模型管理
      gasWellModel: null,
      // 天然气井道模型
      actuatorModel: null,
      // 执行器模型
      // 动画
      animationId: null,
      // 加载状态
      isLoading: true
    };
  },
  created() {
    // 使用Object.defineProperty避免Vue响应式代理Three.js对象
    const threeObjects = ['scene', 'camera', 'renderer', 'controls', 'loader', 'model', 'actuator', 'ground', 'wellEdge', 'wellBarrel', 'wellBackWall', 'wellCover', 'ground2', "wellGround", "bigGround", "pipeline", 'gasParticles',
    // 天然气粒子系统
    'gasWellModel', 'actuatorModel' // 模型管理
    ];
    threeObjects.forEach(key => {
      Object.defineProperty(this, key, {
        value: null,
        writable: true,
        enumerable: false,
        configurable: true
      });
    });
  },
  mounted() {
    // 使用 nextTick 确保 DOM 完全渲染后再初始化
    this.$nextTick(() => {
      // 如果容器尺寸为 0，等待一段时间后重试
      if (this.$refs.threeContainer.clientWidth === 0 || this.$refs.threeContainer.clientHeight === 0) {
        setTimeout(() => {
          this.initThree();
          this.loadModel();
          this.initScene();
        }, 100);
      } else {
        this.initThree();
        this.loadModel();
        this.initScene();
      }
    });
  },
  beforeUnmount() {
    // 清理资源
    this.cleanup();
  },
  methods: {
    initThree() {
      // 检查容器是否存在和尺寸是否有效
      if (!this.$refs.threeContainer) {
        console.error('Three.js 容器不存在');
        return;
      }
      const containerWidth = this.$refs.threeContainer.clientWidth;
      const containerHeight = this.$refs.threeContainer.clientHeight;
      if (containerWidth === 0 || containerHeight === 0) {
        console.warn('容器尺寸为 0，延迟初始化');
        setTimeout(() => {
          this.initThree();
        }, 100);
        return;
      }
      console.log('初始化 Three.js，容器尺寸:', containerWidth, 'x', containerHeight);

      // 创建场景
      this.scene = markRaw(new THREE.Scene());
      // 创建相机
      this.camera = markRaw(new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 1000));
      // 设置相机初始位置，模拟轻微俯视井桶和井盖
      this.camera.position.set(0, 1.8, 2.5); // 相机高度1.8米，距离井桶2.5米，轻微俯视角度
      // 创建渲染器
      this.renderer = markRaw(new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        //设置为false以保持背景不透明
        logarithmicDepthBuffer: true // 改善透明度渲染
      }));
      this.renderer.setSize(containerWidth, containerHeight);
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.renderer.sortObjects = true; // 启用对象排序
      this.renderer.outputColorSpace = THREE.SRGBColorSpace; // 正确的颜色空间
      this.$refs.threeContainer.appendChild(this.renderer.domElement);

      // 创建控制器
      this.controls = markRaw(new OrbitControls(this.camera, this.renderer.domElement));
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;

      // 设置相机控制模式，确保只有相机转动，模型不动
      this.controls.enablePan = false; // 禁用平移
      this.controls.enableZoom = true; // 允许缩放
      this.controls.enableRotate = true; // 允许旋转

      // 限制相机旋转角度，俯视井桶和井盖的视角范围
      this.controls.minAzimuthAngle = -Math.PI / 3; // -60度，限制左右转头范围
      this.controls.maxAzimuthAngle = Math.PI / 3; // +60度
      this.controls.minPolarAngle = Math.PI / 6; // 30度，限制向下旋转，不能看地面
      this.controls.maxPolarAngle = Math.PI / 2; // 90度，可以向上看井盖

      // 设置相机距离限制，防止过度缩放
      this.controls.minDistance = 1.0; // 最小距离
      this.controls.maxDistance = 10.0; // 最大距离

      // 添加光源
      this.addLights();

      // 开始渲染循环
      this.animate();

      // 监听窗口大小变化
      window.addEventListener('resize', this.onWindowResize);
    },
    addLights() {
      // 环境光 - 增强环境光以显示材质细节
      const ambientLight = new THREE.AmbientLight(0x404040, 1.5); // 增强环境光强度
      this.scene.add(ambientLight);

      // 主方向光 - 增强强度以显示高光
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.8); // 增强主光源强度
      directionalLight.position.set(50, 50, 50);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 500;
      directionalLight.shadow.camera.left = -100;
      directionalLight.shadow.camera.right = 100;
      directionalLight.shadow.camera.top = 100;
      directionalLight.shadow.camera.bottom = -100;
      this.scene.add(directionalLight);

      // 补充方向光 - 从不同角度照亮
      const directionalLight2 = new THREE.DirectionalLight(0xffffff, 1.0); // 增强补充光源强度
      directionalLight2.position.set(-30, 30, -30);
      this.scene.add(directionalLight2);

      // 点光源 - 增强强度
      const pointLight = new THREE.PointLight(0xffffff, 1.2, 100); // 增强点光源强度
      pointLight.position.set(0, 20, 0);
      this.scene.add(pointLight);

      // 聚光灯 - 添加聚光灯以增强高光效果
      const spotLight = new THREE.SpotLight(0xffffff, 1.2, 200, Math.PI / 6, 0.3, 1); // 增强聚光灯强度
      spotLight.position.set(30, 40, 30);
      spotLight.target.position.set(0, 0, 0);
      this.scene.add(spotLight);
      this.scene.add(spotLight.target);

      // 半球光 - 模拟天空和地面反射
      const hemisphereLight = new THREE.HemisphereLight(0x87CEEB, 0x8B4513, 0.8); // 增强半球光强度
      this.scene.add(hemisphereLight);

      // 井桶前专用光源 - 在井桶前方添加额外的光源
      this.addWellFrontLights();
    },
    // 在井桶前添加专用光源
    addWellFrontLights() {
      console.log('=== 添加井桶前专用光源 ===');

      // 井桶前主光源 - 从井桶前方照亮
      const wellFrontLight = new THREE.DirectionalLight(0xffffff, 1.5);
      wellFrontLight.position.set(0, 2, 3); // 在井桶前方3米，高度2米
      wellFrontLight.target.position.set(0, 0, 0); // 指向井桶中心
      wellFrontLight.castShadow = true;
      wellFrontLight.shadow.mapSize.width = 1024;
      wellFrontLight.shadow.mapSize.height = 1024;
      wellFrontLight.shadow.camera.near = 0.1;
      wellFrontLight.shadow.camera.far = 50;
      wellFrontLight.shadow.camera.left = -10;
      wellFrontLight.shadow.camera.right = 10;
      wellFrontLight.shadow.camera.top = 10;
      wellFrontLight.shadow.camera.bottom = -10;
      this.scene.add(wellFrontLight);
      this.scene.add(wellFrontLight.target);

      // 井桶前点光源 - 近距离照亮井桶
      const wellPointLight = new THREE.PointLight(0xffffff, 1.0, 20);
      wellPointLight.position.set(0, 1.5, 2); // 在井桶前方2米，高度1.5米
      this.scene.add(wellPointLight);

      // 井桶前聚光灯 - 聚焦照亮井桶
      const wellSpotLight = new THREE.SpotLight(0xffffff, 1.2, 30, Math.PI / 4, 0.2, 1);
      wellSpotLight.position.set(0, 3, 4); // 在井桶前方4米，高度3米
      wellSpotLight.target.position.set(0, 0, 0); // 指向井桶中心
      this.scene.add(wellSpotLight);
      this.scene.add(wellSpotLight.target);

      // 井桶前补光 - 从侧面补光
      const wellSideLight = new THREE.DirectionalLight(0xffffff, 0.8);
      wellSideLight.position.set(2, 1, 1); // 从井桶右前方补光
      wellSideLight.target.position.set(0, 0, 0);
      this.scene.add(wellSideLight);
      this.scene.add(wellSideLight.target);
    },
    loadModel() {
      this.loader = markRaw(new GLTFLoader());
      this.loader.load('/天然气井道1013.glb',
      // '/树.glb',
      gltf => {
        this.model = markRaw(gltf.scene);
        this.gasWellModel = this.model; // 保存天然气井道模型引用
        this.scene.add(this.model);

        // 修复材质渲染问题
        this.fixMaterials();
        // 识别和分组模型组件
        this.identifyModelComponents();
        // 调整相机位置以查看整个场景
        this.fitCameraToModel();
        // 模型加载完成
        this.isLoading = false;
      }, progress => {
        console.log('加载进度:', progress + '%');
      }, error => {
        console.error('模型加载失败:', error);
        this.isLoading = false;
      });
    },
    fixMaterials() {
      console.log('=== 修复材质渲染问题 ===');
      this.model.traverse(child => {
        if (child.isMesh) {
          console.log('处理模型材质:', child.name);
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat, index) => {
                console.log(`  材质 ${index}:`, mat.type, '透明度:', mat.opacity, '透明:', mat.transparent);

                // 修复材质属性
                this.enhanceMaterial(mat, child.name, index);
              });
            } else {
              console.log(`  单一材质:`, child.material.type, '透明度:', child.material.opacity, '透明:', child.material.transparent);

              // 修复材质属性
              this.enhanceMaterial(child.material, child.name, 0);
            }
          }

          // 设置渲染顺序
          if (child.material && (child.material.transparent || Array.isArray(child.material) && child.material.some(mat => mat.transparent))) {
            child.renderOrder = 1000; // 透明对象后渲染
            console.log(`  设置渲染顺序:`, child.renderOrder);
          }
        }
      });
      console.log('材质修复完成');

      // 自动修复UV贴图
      this.fixUVMapping();
    },
    enhanceMaterial(material, meshName, index) {
      try {
        // 根据材质类型和名称进行特殊处理
        const name = meshName.toLowerCase();

        // 玻璃材质处理
        if (name.includes('透明') || name.includes('玻璃') || name.includes('glass') || material.transparent && material.opacity < 1.0) {
          console.log(`      识别为玻璃材质`);
          material.transparent = true;
          material.opacity = Math.max(0.1, material.opacity || 0.8);
          material.side = THREE.DoubleSide;
          material.depthWrite = false;
          material.alphaTest = 0.1;

          // 增强玻璃效果 - 只对支持的材质类型设置高级属性
          if (material.type === 'MeshPhysicalMaterial') {
            material.roughness = 0.1;
            material.metalness = 0.0;
            material.clearcoat = 1.0;
            material.clearcoatRoughness = 0.1;
            material.ior = 1.5; // 玻璃的折射率
            material.transmission = 0.9;
            material.thickness = 0.5;
          } else if (material.type === 'MeshStandardMaterial') {
            material.roughness = 0.1;
            material.metalness = 0.0;
            material.clearcoat = 1.0;
            material.clearcoatRoughness = 0.1;
          }
        }

        // 金属材质处理
        else if (name.includes('金属') || name.includes('metal') || name.includes('钢') || name.includes('铁')) {
          console.log(`      识别为金属材质`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            material.metalness = 0.9;
            material.roughness = 0.1;
            material.clearcoat = 0.8;
            material.clearcoatRoughness = 0.1;
          }
        }

        // 木头材质处理
        else if (name.includes('木') || name.includes('wood') || name.includes('树')) {
          console.log(`      识别为木头材质`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            material.metalness = 0.0;
            material.roughness = 0.8;
            material.clearcoat = 0.3;
            material.clearcoatRoughness = 0.5;
          }
        }

        // 塑料材质处理
        else if (name.includes('塑料') || name.includes('plastic')) {
          console.log(`      识别为塑料材质`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            material.metalness = 0.0;
            material.roughness = 0.3;
            material.clearcoat = 0.5;
            material.clearcoatRoughness = 0.2;
          }
        }

        // 地块材质处理
        else if (name.includes('地块') || name.includes('ground') || name.includes('土地')) {
          console.log(`      识别为地块材质`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            material.metalness = 0.0;
            material.roughness = 0.9;
            material.clearcoat = 0.0;
            material.clearcoatRoughness = 0.0;

            // 确保UV贴图正确显示
            if (material.map) {
              material.map.wrapS = THREE.RepeatWrapping;
              material.map.wrapT = THREE.RepeatWrapping;
              material.map.repeat.set(1, 1);
              material.map.flipY = false;
              console.log(`      地块贴图已设置: ${material.map.image?.src}`);
            }

            // 设置法线贴图
            if (material.normalMap) {
              material.normalMap.wrapS = THREE.RepeatWrapping;
              material.normalMap.wrapT = THREE.RepeatWrapping;
              material.normalMap.repeat.set(1, 1);
              material.normalMap.flipY = false;
              material.normalScale = new THREE.Vector2(1, 1);
            }

            // 设置环境光遮蔽贴图
            if (material.aoMap) {
              material.aoMap.wrapS = THREE.RepeatWrapping;
              material.aoMap.wrapT = THREE.RepeatWrapping;
              material.aoMap.repeat.set(1, 1);
              material.aoMap.flipY = false;
              material.aoMapIntensity = 1.0;
            }
          }
        }

        // 植被材质处理（包括草、树、植物等）
        else if (name.includes('草') || name.includes('grass') || name.includes('植被') || name.includes('树') || name.includes('tree') || name.includes('植物') || name.includes('绿化') || name.includes('叶子') || name.includes('leaf') || name.includes('叶') || name.includes('枝') || name.includes('branch')) {
          console.log(`      识别为植被材质: ${name}`);
          console.log(`      当前材质颜色: ${material.color.getHexString()}`);
          console.log(`      是否有贴图: ${!!material.map}`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            // 植被材质属性
            material.metalness = 0.0;
            material.roughness = 0.9; // 植被表面相对粗糙
            material.clearcoat = 0.0;
            material.clearcoatRoughness = 0.0;

            // 设置植被颜色
            const nameLower = name.toLowerCase();
            if (!material.map) {
              // 根据植被类型设置不同的颜色
              if (nameLower.includes('叶子') || nameLower.includes('leaf') || nameLower.includes('叶') || nameLower.includes('枝')) {
                // 树叶使用更自然的绿色
                material.color.setHex(0x228B22); // 森林绿
                console.log(`      设置树叶颜色: ${material.color.getHexString()}`);
              } else if (nameLower.includes('树') || nameLower.includes('tree')) {
                // 树干使用棕色
                material.color.setHex(0x8B4513); // 马鞍棕
                console.log(`      设置树干颜色: ${material.color.getHexString()}`);
              } else if (nameLower.includes('草') || nameLower.includes('grass')) {
                // 草使用草绿色
                material.color.setHex(0x32CD32); // 酸橙绿
                console.log(`      设置草颜色: ${material.color.getHexString()}`);
              } else {
                // 其他植被使用深绿色
                material.color.setHex(0x4a7c59); // 深绿色
                console.log(`      设置植被默认颜色: ${material.color.getHexString()}`);
              }
            } else {
              // 如果有贴图，调整颜色以增强效果
              if (nameLower.includes('叶子') || nameLower.includes('leaf') || nameLower.includes('叶') || nameLower.includes('枝')) {
                // 增强树叶贴图的绿色调
                material.color.setHex(0x90EE90); // 浅绿色，增强贴图效果
                console.log(`      增强树叶贴图颜色: ${material.color.getHexString()}`);
              } else if (nameLower.includes('树') || nameLower.includes('tree')) {
                // 增强树干贴图的棕色调
                material.color.setHex(0xD2691E); // 巧克力色，增强贴图效果
                console.log(`      增强树干贴图颜色: ${material.color.getHexString()}`);
              }
            }

            // 确保UV贴图正确显示
            if (material.map) {
              material.map.wrapS = THREE.RepeatWrapping;
              material.map.wrapT = THREE.RepeatWrapping;

              // 根据植被类型设置不同的重复次数
              if (name.includes('树') || name.includes('tree')) {
                material.map.repeat.set(1, 1); // 树纹理通常不需要重复
              } else {
                material.map.repeat.set(2, 2); // 草纹理通常需要重复
              }
              material.map.flipY = false;
              console.log(`      植被贴图已设置: ${material.map.image?.src}`);
            }

            // 设置法线贴图
            if (material.normalMap) {
              material.normalMap.wrapS = THREE.RepeatWrapping;
              material.normalMap.wrapT = THREE.RepeatWrapping;
              material.normalMap.repeat.set(1, 1);
              material.normalMap.flipY = false;
              material.normalScale = new THREE.Vector2(1, 1);
            }

            // 设置环境光遮蔽贴图
            if (material.aoMap) {
              material.aoMap.wrapS = THREE.RepeatWrapping;
              material.aoMap.wrapT = THREE.RepeatWrapping;
              material.aoMap.repeat.set(1, 1);
              material.aoMap.flipY = false;
              material.aoMapIntensity = 0.8;
            }

            // 植被材质特殊处理
            material.side = THREE.DoubleSide; // 植被通常需要双面渲染
            material.transparent = false;
            material.opacity = 1.0;
            console.log(`      植被材质已优化: ${name}`);
          }
        }

        // 土层材质处理
        else if (name.includes('土') || name.includes('soil') || name.includes('dirt') || name.includes('层')) {
          console.log(`      识别为土层材质`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            material.metalness = 0.0;
            material.roughness = 0.9;
            material.clearcoat = 0.0;
            material.clearcoatRoughness = 0.0;

            // 确保UV贴图正确显示
            if (material.map) {
              material.map.wrapS = THREE.RepeatWrapping;
              material.map.wrapT = THREE.RepeatWrapping;
              material.map.repeat.set(1, 1);
              material.map.flipY = false;
              console.log(`      土层贴图已设置: ${material.map.image?.src}`);
            }

            // 设置法线贴图
            if (material.normalMap) {
              material.normalMap.wrapS = THREE.RepeatWrapping;
              material.normalMap.wrapT = THREE.RepeatWrapping;
              material.normalMap.repeat.set(1, 1);
              material.normalMap.flipY = false;
              material.normalScale = new THREE.Vector2(1, 1);
            }

            // 设置环境光遮蔽贴图
            if (material.aoMap) {
              material.aoMap.wrapS = THREE.RepeatWrapping;
              material.aoMap.wrapT = THREE.RepeatWrapping;
              material.aoMap.repeat.set(1, 1);
              material.aoMap.flipY = false;
              material.aoMapIntensity = 1.0;
            }
          }
        }

        // 通用材质增强
        else {
          console.log(`      通用材质增强`);
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            // 确保有合理的高光值
            if (material.roughness === undefined || material.roughness === 0) {
              material.roughness = 0.5;
            }
            if (material.metalness === undefined) {
              material.metalness = 0.0;
            }

            // 增强环境光遮蔽
            if (material.aoMap) {
              material.aoMapIntensity = 1.0;
            }

            // 增强法线贴图
            if (material.normalMap) {
              material.normalScale = new THREE.Vector2(1, 1);
            }
          }
        }

        // 验证材质属性值的有效性
        this.validateMaterialProperties(material);

        // 确保材质正确更新
        material.needsUpdate = true;
        console.log(`      材质增强完成: 金属度=${material.metalness}, 粗糙度=${material.roughness}, 透明度=${material.opacity}`);
      } catch (error) {
        console.error(`      材质增强失败:`, error);
        // 如果增强失败，至少确保基本属性有效
        this.validateMaterialProperties(material);
        material.needsUpdate = true;
      }
    },
    validateMaterialProperties(material) {
      // 验证并修复材质属性值
      if (material.roughness !== undefined) {
        material.roughness = Math.max(0, Math.min(1, material.roughness));
      }
      if (material.metalness !== undefined) {
        material.metalness = Math.max(0, Math.min(1, material.metalness));
      }
      if (material.opacity !== undefined) {
        material.opacity = Math.max(0, Math.min(1, material.opacity));
      }
      if (material.clearcoat !== undefined) {
        material.clearcoat = Math.max(0, Math.min(1, material.clearcoat));
      }
      if (material.clearcoatRoughness !== undefined) {
        material.clearcoatRoughness = Math.max(0, Math.min(1, material.clearcoatRoughness));
      }
      if (material.ior !== undefined) {
        material.ior = Math.max(1, Math.min(2.33, material.ior));
      }
      if (material.transmission !== undefined) {
        material.transmission = Math.max(0, Math.min(1, material.transmission));
      }
      if (material.thickness !== undefined) {
        material.thickness = Math.max(0, material.thickness);
      }
      if (material.alphaTest !== undefined) {
        material.alphaTest = Math.max(0, Math.min(1, material.alphaTest));
      }
    },
    identifyModelComponents() {
      if (!this.model) return;
      console.log('模型对象:', this.model);
      let groupCount = 0;
      let meshCount = 0;
      const allGroups = [];
      const allMeshes = [];

      // 遍历所有对象，查找Group和Mesh
      this.model.traverse(child => {
        if (child.isGroup) {
          groupCount++;
          const groupName = child.name;
          const groupNameLower = groupName.toLowerCase();
          allGroups.push({
            name: groupName,
            nameLower: groupNameLower,
            object: child
          });
          // 根据分组名称进行精确匹配
          if (groupNameLower === '仪器') {
            this.actuator = child;
            this.actuatorModel = child; // 同时设置actuatorModel引用
            child.userData.type = 'actuator';

            // 保存执行器的初始位置、旋转和缩放（强制保存，确保是最新的）
            child.userData.initialPosition = child.position.clone();
            child.userData.initialRotation = child.rotation.clone();
            child.userData.initialScale = child.scale.clone();

            // 同时保存世界坐标信息
            const worldPosition = new THREE.Vector3();
            const worldQuaternion = new THREE.Quaternion();
            const worldScale = new THREE.Vector3();
            child.getWorldPosition(worldPosition);
            child.getWorldQuaternion(worldQuaternion);
            child.getWorldScale(worldScale);
            child.userData.initialWorldPosition = worldPosition.clone();
            child.userData.initialWorldQuaternion = worldQuaternion.clone();
            child.userData.initialWorldScale = worldScale.clone();
            console.log('=== 保存执行器初始状态 ===');
            console.log('执行器分组名称:', groupName);
            console.log('执行器父级:', child.parent ? child.parent.name : '无父级');
            console.log('执行器本地位置:', child.position);
            console.log('执行器本地旋转:', child.rotation);
            console.log('执行器本地缩放:', child.scale);
            console.log('执行器世界位置:', worldPosition);
            console.log('执行器世界旋转:', worldQuaternion);
            console.log('执行器世界缩放:', worldScale);
            console.log('保存的初始本地位置:', child.userData.initialPosition);
            console.log('保存的初始本地旋转:', child.userData.initialRotation);
            console.log('保存的初始本地缩放:', child.userData.initialScale);
            console.log('保存的初始世界位置:', child.userData.initialWorldPosition);
            console.log('保存的初始世界旋转:', child.userData.initialWorldQuaternion);
            console.log('保存的初始世界缩放:', child.userData.initialWorldScale);
            console.log('=== 执行器初始状态保存完成 ===');
          } else if (groupNameLower === '地块') {
            this.ground = child;
            child.userData.type = 'ground';
            console.log('大地分组:', groupName);
          } else if (groupNameLower === '井盖') {
            this.wellCover = child;
            child.userData.type = 'wellCover';
            console.log('井盖分组:', groupName);
          } else {
            console.log('未识别的分组:', groupName);
          }
        } else if (child.isMesh) {
          meshCount++;
          const meshName = child.name;
          const meshNameLower = meshName.toLowerCase();
          // 井桶模型可能是Mesh而不是Group
          if (meshNameLower === '井-前半') {
            this.wellBarrel = child;
            child.userData.type = 'wellBarrel';
            console.log('找到井桶模型:', meshName);
          } else if (meshNameLower === '井-后半') {
            this.wellBackWall = child;
            child.userData.type = 'wellBackWall';
            console.log('找到井桶后壁模型:', meshName);
          } else if (meshNameLower === '地层2') {
            this.ground2 = child;
            child.userData.type = 'ground2';
            console.log('找到地层2模型:', meshName);
          } else if (meshNameLower === '井内土层') {
            this.wellGround = child;
            child.userData.type = 'wellGround';
            console.log('找到地层2模型:', meshName);
          } else if (meshNameLower === '土层-大') {
            this.bigGround = child;
            child.userData.type = 'bigGround';
            console.log('土层-大模型:', meshName);
          } else if (meshNameLower === '管道') {
            this.pipeline = child;
            child.userData.type = 'pipeline';
            console.log('找到管道模型:', meshName);
          } else if (meshNameLower === '井口圈') {
            this.wellEdge = child;
            child.userData.type = 'wellEdge';
            console.log('找到井盖边缘分组:', meshName);
          }
        }
      });
      console.log(`总共找到 ${groupCount} 个分组对象，${meshCount} 个网格对象`);

      // 设置井桶透明显示
      this.setWellBarrelTransparency();
    },
    setWellBarrelTransparency() {
      console.log('=== 设置井桶透明显示 ===');

      // 设置井桶前半部分透明
      if (this.wellBarrel) {
        console.log('设置井桶前半部分透明');
        this.wellBarrel.traverse(child => {
          if (child.isMesh && child.material) {
            // 保存原始材质属性
            if (!child.userData.originalMaterial) {
              child.userData.originalMaterial = {
                transparent: child.material.transparent,
                opacity: child.material.opacity,
                side: child.material.side,
                depthWrite: child.material.depthWrite
              };
            }

            // 设置透明属性
            child.material.transparent = true;
            child.material.opacity = 0.2; // 设置透明度为20%
            // child.material.side = THREE.DoubleSide
            // child.material.depthWrite = false
            // child.material.needsUpdate = true

            console.log('井桶前半部分材质已设置为透明');
          }
        });
      }
      // 设置地层透明
      if (this.bigGround) {
        console.log('设置井桶前半部分透明');
        this.bigGround.traverse(child => {
          if (child.isMesh && child.material) {
            // 保存原始材质属性
            if (!child.userData.originalMaterial) {
              child.userData.originalMaterial = {
                transparent: child.material.transparent,
                opacity: child.material.opacity,
                side: child.material.side,
                depthWrite: child.material.depthWrite
              };
            }

            // 设置透明属性
            child.material.transparent = true;
            child.material.opacity = 0.7; // 设置透明度为20%
            child.material.side = THREE.DoubleSide;
            child.material.depthWrite = false;
            child.material.needsUpdate = true;
            console.log('井桶前半部分材质已设置为透明');
          }
        });
      }
    },
    fitCameraToModel() {
      if (!this.model) return;

      // 设置相机位置，模拟轻微俯视井桶和井盖
      // 相机位置：正对井桶，轻微俯视高度，可以看到井盖
      this.camera.position.set(0, 1.8, 1.8);

      // 让相机看向井桶中心位置
      this.camera.lookAt(0, 0, 0);
      this.controls.target.set(0, 0.8, 0);
    },
    fixUVMapping() {
      console.log('=== 修复UV贴图 ===');
      if (!this.model) {
        console.error('模型未加载');
        return;
      }
      let fixedCount = 0;
      this.model.traverse(child => {
        if (child.isMesh && child.material) {
          const name = child.name.toLowerCase();
          console.log(`检查网格UV: ${child.name}`);

          // 处理材质数组
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material, index) => {
            if (material && material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
              console.log(`  修复材质 ${index}: ${material.type}`);

              // 修复所有贴图的UV设置
              const maps = [{
                map: material.map,
                name: 'diffuse'
              }, {
                map: material.normalMap,
                name: 'normal'
              }, {
                map: material.aoMap,
                name: 'ao'
              }, {
                map: material.roughnessMap,
                name: 'roughness'
              }, {
                map: material.metalnessMap,
                name: 'metalness'
              }, {
                map: material.emissiveMap,
                name: 'emissive'
              }, {
                map: material.bumpMap,
                name: 'bump'
              }, {
                map: material.displacementMap,
                name: 'displacement'
              }];
              maps.forEach(({
                map,
                name
              }) => {
                if (map) {
                  // 设置正确的UV包装模式
                  map.wrapS = THREE.RepeatWrapping;
                  map.wrapT = THREE.RepeatWrapping;

                  // 根据材质类型设置重复次数
                  if (name.includes('草') || name.includes('grass')) {
                    map.repeat.set(2, 2);
                  } else if (name.includes('地块') || name.includes('ground')) {
                    map.repeat.set(1, 1);
                  } else {
                    map.repeat.set(1, 1);
                  }

                  // 确保贴图不翻转
                  map.flipY = false;

                  // 设置过滤模式
                  map.minFilter = THREE.LinearMipmapLinearFilter;
                  map.magFilter = THREE.LinearFilter;

                  // 生成mipmap
                  map.generateMipmaps = true;
                  map.needsUpdate = true;
                  console.log(`    ${name}贴图UV已修复: ${map.image?.src}`);
                  fixedCount++;
                }
              });

              // 特殊处理法线贴图
              if (material.normalMap) {
                material.normalScale = new THREE.Vector2(1, 1);
              }

              // 特殊处理环境光遮蔽贴图
              if (material.aoMap) {
                material.aoMapIntensity = 1.0;
              }

              // 只在材质真正需要更新时才设置
              if (material.needsUpdate !== true) {
                material.needsUpdate = true;
              }
            }
          });
        }
      });
      console.log(`UV贴图修复完成，共修复 ${fixedCount} 个贴图`);

      // 强制重新渲染
      this.renderer.render(this.scene, this.camera);
    },
    focusOnActuator() {
      if (!this.actuator) {
        return;
      }
      const box = new THREE.Box3().setFromObject(this.actuator);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = this.camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

      // 根据是否在查看模式调整相机距离
      if (this.isViewingActuator) {
        cameraZ *= 2.2; // 查看模式时更近一些，让执行器看起来更合适
      } else {
        cameraZ *= 2; // 正常模式时更远一些
      }

      // 确保相机距离不会太小
      cameraZ = Math.max(cameraZ, 1.0);

      // 设置相机位置，让执行器在场景中间
      if (this.isViewingActuator) {
        // 查看模式：将执行器放在场景中心，相机围绕执行器
        console.log('查看模式：执行器中心位置:', center);
        console.log('执行器尺寸:', size);
        console.log('计算相机距离:', cameraZ);

        // 将执行器移动到场景中心 (0, 0, 0)
        const currentPosition = this.actuator.position.clone();
        const offsetToCenter = new THREE.Vector3(0, 0, 0).sub(center);
        this.actuator.position.add(offsetToCenter);

        // 更新执行器的世界坐标
        this.actuator.updateMatrixWorld();

        // 相机围绕执行器中心 (0, 0, 0) 放置
        this.camera.position.set(cameraZ, 0, 0); // 从右侧看向执行器
        this.camera.lookAt(0, 0, 0); // 看向场景中心
        this.controls.target.set(0, 0, 0); // 控制器目标设为场景中心

        console.log('执行器已移动到场景中心:', this.actuator.position);
        console.log('相机位置:', this.camera.position);
        console.log('控制器目标:', this.controls.target);
      } else {
        // 正常模式：相机看向执行器中心
        this.camera.position.set(center.x + cameraZ, center.y + cameraZ * 0.8, center.z + cameraZ);
        this.camera.lookAt(center);
        this.controls.target.copy(center);
        console.log('正常模式：相机聚焦到执行器中心:', center);
      }
      // 强制更新控制器
      this.controls.update();
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);

      // 只在控制器需要更新时才更新
      if (this.controls.enabled) {
        this.controls.update();
      }

      // 执行器旋转动画
      if (this.actuatorRotating && this.actuator) {
        this.actuator.rotation.y += this.actuatorRotationSpeed;
      }

      // 只在场景有变化时才渲染
      this.renderer.render(this.scene, this.camera);
    },
    onWindowResize() {
      if (!this.camera || !this.renderer || !this.$refs.threeContainer) return;
      const containerWidth = this.$refs.threeContainer.clientWidth;
      const containerHeight = this.$refs.threeContainer.clientHeight;

      // 如果容器尺寸为 0，跳过更新
      if (containerWidth === 0 || containerHeight === 0) {
        console.warn('窗口大小调整时容器尺寸为 0，跳过更新');
        return;
      }
      this.camera.aspect = containerWidth / containerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(containerWidth, containerHeight);
    },
    // 井盖控制功能
    toggleWellCover() {
      if (!this.wellCover) {
        console.error('井盖对象不存在，无法操作');
        return;
      }
      console.log('=== 切换井盖状态 ===');
      console.log('当前井盖状态:', this.wellCoverOpen ? '打开' : '关闭');
      if (this.wellCoverOpen) {
        this.closeWellCover();
      } else {
        this.openWellCover();
      }
    },
    // 井桶显示/隐藏控制功能
    toggleWellBarrel() {
      if (!this.wellBarrel && !this.wellBackWall) {
        console.error('井桶对象不存在，无法操作');
        return;
      }
      if (this.wellBarrelHidden) {
        this.showWellBarrel();
      } else {
        this.hideWellBarrel();
      }
    },
    // 隐藏井桶
    hideWellBarrel() {
      // 隐藏井桶前半部分
      if (this.wellBarrel) {
        this.wellBarrel.visible = false;
      }
      // 隐藏井口圈
      if (this.wellEdge) {
        this.wellEdge.visible = false;
      }
      // 隐藏井桶后壁
      // if (this.wellBackWall) {
      //   this.wellBackWall.visible = false
      //   console.log('井桶后壁已隐藏')
      // }

      this.wellBarrelHidden = true;
    },
    // 显示井桶
    showWellBarrel() {
      // 显示井桶前半部分
      if (this.wellBarrel) {
        this.wellBarrel.visible = true;
      }
      // 显示井口圈
      if (this.wellEdge) {
        this.wellEdge.visible = true;
      }
      // 显示井桶后壁
      // if (this.wellBackWall) {
      //   this.wellBackWall.visible = true
      // }

      this.wellBarrelHidden = false;
    },
    openWellCover() {
      if (!this.wellCover) {
        return;
      }

      // 保存井盖的原始位置和旋转（如果还没有保存）
      if (!this.wellCover.userData.originalPosition) {
        this.wellCover.userData.originalPosition = this.wellCover.position.clone();
        this.wellCover.userData.originalRotation = this.wellCover.rotation.clone();
        console.log('保存井盖原始位置:', this.wellCover.userData.originalPosition);
        console.log('保存井盖原始旋转:', this.wellCover.userData.originalRotation);
      }

      // 计算井盖的包围盒，找到旋转轴（井盖边缘）
      const box = new THREE.Box3().setFromObject(this.wellCover);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      console.log('井盖包围盒:', {
        center,
        size
      });

      // 设置旋转轴为井盖边缘（假设井盖是圆形，旋转轴在边缘）
      const rotationAxis = new THREE.Vector3(1, 0, 0); // 绕X轴旋转
      const rotationPoint = new THREE.Vector3(center.x + size.x / 2,
      // 井盖边缘
      center.y, center.z);
      console.log('旋转轴:', rotationAxis);
      console.log('旋转点:', rotationPoint);

      // 动画参数
      const totalDuration = 3000; // 总动画时长3秒
      const startTime = Date.now();

      // 目标位置和旋转
      const liftHeight = 0.3; // 向上抬起0.3米（模拟从井口拿起）
      const horizontalDistance = 1.2; // 水平移动到旁边1.2米
      // 不需要旋转，直接放下

      console.log('开始井盖打开动画 - 三阶段：从井口拿起->移动到旁边->直接放下');
      console.log('从井口拿起高度:', liftHeight, '米');
      console.log('移动到旁边距离:', horizontalDistance, '米');
      console.log('保持原始旋转角度，不进行旋转');
      const animateOpen = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        let currentY, currentX, currentZ, currentRotationX;
        if (progress <= 0.33) {
          // 第一阶段：从井口拿起 (0-33%) - 沿Z轴抬高
          const phaseProgress = progress / 0.33;
          const easeProgress = 1 - Math.pow(1 - phaseProgress, 2); // easeOutQuad

          currentY = this.wellCover.userData.originalPosition.y;
          currentX = this.wellCover.userData.originalPosition.x;
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight * easeProgress; // Z轴抬高
          currentRotationX = this.wellCover.userData.originalRotation.x;
        } else if (progress <= 0.66) {
          // 第二阶段：移动到旁边 (33-66%) - 向反方向移动
          const phaseProgress = (progress - 0.33) / 0.33;
          const easeProgress = 1 - Math.pow(1 - phaseProgress, 2); // easeOutQuad

          currentY = this.wellCover.userData.originalPosition.y;
          currentX = this.wellCover.userData.originalPosition.x - horizontalDistance * easeProgress; // 向反方向移动
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight; // 保持Z轴抬起高度
          currentRotationX = this.wellCover.userData.originalRotation.x;
        } else {
          // 第三阶段：直接放下 (66-100%)
          const phaseProgress = (progress - 0.66) / 0.34;
          const easeProgress = 1 - Math.pow(1 - phaseProgress, 3); // easeOutCubic

          currentY = this.wellCover.userData.originalPosition.y;
          currentX = this.wellCover.userData.originalPosition.x - horizontalDistance; // 保持反方向旁边位置
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight * (1 - easeProgress) + 0.03; // Z轴下降
          currentRotationX = this.wellCover.userData.originalRotation.x; // 保持原始旋转
        }

        // 应用位置和旋转
        this.wellCover.position.set(currentX, currentY, currentZ);
        this.wellCover.rotation.x = currentRotationX;

        // 添加轻微的震动效果（主要在抬高阶段）
        if (progress < 0.4) {
          const shakeIntensity = 0.015 * (1 - progress); // 震动强度随进度递减
          this.wellCover.position.x += (Math.random() - 0.5) * shakeIntensity;
          this.wellCover.position.z += (Math.random() - 0.5) * shakeIntensity;
        }
        if (progress < 1) {
          requestAnimationFrame(animateOpen);
        } else {
          // 动画完成
          this.wellCoverOpen = true;
        }
      };
      requestAnimationFrame(animateOpen);
    },
    closeWellCover() {
      if (!this.wellCover) {
        return;
      }
      if (!this.wellCover.userData.originalPosition || !this.wellCover.userData.originalRotation) {
        return;
      }

      // 动画参数
      const totalDuration = 2500; // 总动画时长2.5秒（关闭比打开快一些）
      const startTime = Date.now();
      const liftHeight = 0.3; // 抬起高度，与打开动画保持一致

      // 起始位置和旋转
      const startY = this.wellCover.position.y;
      const startX = this.wellCover.position.x;
      const animateClose = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        let currentY, currentX, currentZ, currentRotationX;
        if (progress <= 0.33) {
          // 第一阶段：从旁边拿起 (0-33%)
          const phaseProgress = progress / 0.33;
          const easeProgress = Math.pow(phaseProgress, 2); // easeInQuad

          currentY = startY; // 保持Y轴位置
          currentX = startX; // 保持旁边位置
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight * easeProgress + 0.03; // Z轴抬高
          currentRotationX = this.wellCover.userData.originalRotation.x; // 保持原始旋转

          console.log(`阶段1-从旁边拿起: ${(phaseProgress * 100).toFixed(1)}%`);
        } else if (progress <= 0.66) {
          // 第二阶段：移回井口 (33-66%) - 从反方向移回
          const phaseProgress = (progress - 0.33) / 0.33;
          const easeProgress = Math.pow(phaseProgress, 2); // easeInQuad
          currentY = startY; // 保持Y轴位置
          currentX = startX + (this.wellCover.userData.originalPosition.x - startX) * easeProgress; // 从反方向移回井口
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight; // 保持Z轴抬起高度
          currentRotationX = this.wellCover.userData.originalRotation.x; // 保持原始旋转
        } else {
          // 第三阶段：放回井口 (66-100%) - 沿Z轴下降
          const phaseProgress = (progress - 0.66) / 0.34;
          const easeProgress = Math.pow(phaseProgress, 3); // easeInCubi
          currentY = startY; // 保持Y轴位置
          currentX = this.wellCover.userData.originalPosition.x; // 保持井口位置
          currentZ = this.wellCover.userData.originalPosition.z + liftHeight * (1 - easeProgress); // Z轴下降
          currentRotationX = this.wellCover.userData.originalRotation.x; // 保持原始旋转
        }
        // 应用位置和旋转
        this.wellCover.position.set(currentX, currentY, currentZ);
        this.wellCover.rotation.x = currentRotationX;
        // 添加轻微的震动效果（主要在下降阶段）
        if (progress > 0.6) {
          const shakeIntensity = 0.01 * (1 - progress);
          this.wellCover.position.x += (Math.random() - 0.5) * shakeIntensity;
          this.wellCover.position.z += (Math.random() - 0.5) * shakeIntensity;
        }
        if (progress < 1) {
          requestAnimationFrame(animateClose);
        } else {
          // 动画完成，确保精确回到原始位置
          this.wellCover.position.copy(this.wellCover.userData.originalPosition);
          this.wellCover.rotation.copy(this.wellCover.userData.originalRotation);
          this.wellCoverOpen = false;
        }
      };
      requestAnimationFrame(animateClose);
    },
    // 查看执行器模式
    viewActuator() {
      if (!this.actuator) {
        return;
      }
      // 先更新状态，确保后续方法能正确识别查看模式
      this.isViewingActuator = true;

      // 隐藏所有其他模型，只保留执行器
      this.hideAllModelsExceptActuator();

      // 强制显示执行器模型
      this.forceShowActuator();

      // 调整相机聚焦到执行器
      this.focusOnActuator();

      // 设置相机控制为执行器查看模式
      this.setCameraControlsForActuatorView();

      // 自动开始执行器旋转
      this.startActuatorRotation();
    },
    // 切换执行器旋转状态
    toggleActuatorRotation() {
      if (!this.actuator) {
        console.error('执行器对象不存在，无法控制旋转');
        return;
      }
      if (this.actuatorRotating) {
        this.stopActuatorRotation();
      } else {
        this.startActuatorRotation();
      }
    },
    // 开始执行器旋转
    startActuatorRotation() {
      console.log('=== 开始执行器旋转 ===');
      if (!this.actuator) {
        console.warn('执行器模型不存在，无法开始旋转');
        return;
      }
      this.actuatorRotating = true;
      console.log('执行器旋转已开始');
    },
    // 停止执行器旋转
    stopActuatorRotation() {
      console.log('=== 停止执行器旋转 ===');
      this.actuatorRotating = false;
      console.log('执行器旋转已停止');
    },
    // 获取状态样式类
    getStatusClass() {
      const status = this.actuatorMetrics.status;
      if (status === '正常') return 'normal';
      if (status === '警告') return 'warning';
      if (status === '故障') return 'error';
      return 'normal';
    },
    // 强制显示执行器模型
    forceShowActuator() {
      if (!this.actuator) {
        return;
      }

      // 强制设置执行器可见
      this.actuator.visible = true;

      // 遍历所有子对象，强制设置可见
      this.actuator.traverse(child => {
        if (child.isMesh || child.isGroup) {
          child.visible = true;
          // 如果是网格，确保材质也正确
          if (child.isMesh && child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat, index) => {
                mat.transparent = false;
                mat.opacity = 1.0;
                mat.needsUpdate = true;
              });
            } else {
              child.material.transparent = false;
              child.material.opacity = 1.0;
              child.material.needsUpdate = true;
            }
          }
        }
      });

      // 确保执行器在场景中
      if (!this.scene.children.includes(this.actuator)) {
        this.scene.add(this.actuator);
      }
    },
    // 初始化场景
    initScene() {
      // 设置场景背景
      this.setupSceneBackground();

      // 重置相机位置
      this.resetCameraPosition();
      // 显示所有模型
      this.showAllModels();

      // 设置相机控制为正常模式
      this.setCameraControlsForNormalMode();

      // 停止执行器旋转
      this.stopActuatorRotation();
      console.log('=== 初始化场景完成 ===');
    },
    // 设置场景背景
    setupSceneBackground() {
      const loader = new THREE.TextureLoader();
      loader.load('https://www.mz.sgact.cn:9000/tianyi/2025/11/05/327dbffe76ab4ad4bea363d68456af6a.jpg', texture => {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.flipY = false; // 防止纹理翻转
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        // 直接设置为背景，保持原始透明度
        this.scene.background = texture;
      });
    },
    // 重置相机位置
    resetCameraPosition() {
      this.fitCameraToModel();
    },
    // 显示所有模型
    showAllModels() {
      // 显示天然气井道模型
      if (this.gasWellModel) {
        this.gasWellModel.visible = true;
      }

      // 显示所有天然气井道组件
      const modelsToShow = [{
        model: this.ground,
        name: '大地'
      }, {
        model: this.wellEdge,
        name: '井盖边缘'
      }, {
        model: this.wellBarrel,
        name: '井桶'
      }, {
        model: this.wellBackWall,
        name: '井桶后壁'
      }, {
        model: this.wellCover,
        name: '井盖'
      }, {
        model: this.ground2,
        name: '地层2'
      }, {
        model: this.wellGround,
        name: '井内土层'
      }, {
        model: this.bigGround,
        name: '土层-大'
      }, {
        model: this.pipeline,
        name: '管道'
      }, {
        model: this.actuator,
        name: '执行器'
      }];
      modelsToShow.forEach(({
        model,
        name
      }) => {
        if (model) {
          model.visible = true;
          console.log(`${name}模型已显示`);
        }
      });

      // 重置所有模型到初始状态
      this.resetAllModelsToInitialState();

      // 重置查看状态
      this.isViewingActuator = false;
    },
    // 重置所有模型到初始状态
    resetAllModelsToInitialState() {
      // 重置井盖到初始状态
      this.resetWellCoverToInitialState();

      // 重置执行器到初始状态
      this.tryWorldCoordinateReset();

      // 重置井桶透明度
      this.setWellBarrelTransparency();
      this.wellBarrelHidden = false; //初始化井桶显示状态控制

      // 停止执行器旋转
      this.stopActuatorRotation();
    },
    // 重置井盖到初始状态
    resetWellCoverToInitialState() {
      if (!this.wellCover) {
        return;
      }

      // 恢复井盖到原始位置和旋转
      if (this.wellCover.userData.originalPosition) {
        this.wellCover.position.copy(this.wellCover.userData.originalPosition);
      }
      if (this.wellCover.userData.originalRotation) {
        this.wellCover.rotation.copy(this.wellCover.userData.originalRotation);
      }

      // 重置井盖状态
      this.wellCoverOpen = false;
    },
    // 尝试使用世界坐标进行重置
    tryWorldCoordinateReset() {
      if (!this.actuator || !this.actuator.userData.initialWorldPosition) {
        console.warn('无法进行世界坐标重置：缺少初始世界坐标数据');
        return;
      }
      // 获取当前世界坐标
      const currentWorldPosition = new THREE.Vector3();
      const currentWorldQuaternion = new THREE.Quaternion();
      const currentWorldScale = new THREE.Vector3();
      this.actuator.getWorldPosition(currentWorldPosition);
      this.actuator.getWorldQuaternion(currentWorldQuaternion);
      this.actuator.getWorldScale(currentWorldScale);

      // 计算需要调整的本地变换
      if (this.actuator.parent) {
        // 如果有父级，需要计算相对于父级的变换
        console.log('执行器有父级，计算相对变换');

        // 确保父级的世界矩阵是最新的
        this.actuator.parent.updateMatrixWorld();

        // 创建逆变换矩阵
        const parentInverseMatrix = new THREE.Matrix4();
        parentInverseMatrix.copy(this.actuator.parent.matrixWorld);
        parentInverseMatrix.invert();

        // 计算目标本地位置
        const targetLocalPosition = new THREE.Vector3();
        targetLocalPosition.copy(this.actuator.userData.initialWorldPosition);
        targetLocalPosition.applyMatrix4(parentInverseMatrix);

        // 计算目标本地旋转
        const targetLocalQuaternion = new THREE.Quaternion();
        targetLocalQuaternion.copy(this.actuator.userData.initialWorldQuaternion);
        const parentWorldQuaternion = new THREE.Quaternion();
        this.actuator.parent.getWorldQuaternion(parentWorldQuaternion);
        const parentInverseQuaternion = parentWorldQuaternion.clone().invert();
        targetLocalQuaternion.premultiply(parentInverseQuaternion);

        // 计算目标本地缩放
        const targetLocalScale = new THREE.Vector3();
        targetLocalScale.copy(this.actuator.userData.initialWorldScale);
        const parentWorldScale = new THREE.Vector3();
        this.actuator.parent.getWorldScale(parentWorldScale);
        targetLocalScale.divide(parentWorldScale);

        // 应用变换
        this.actuator.position.copy(targetLocalPosition);
        this.actuator.quaternion.copy(targetLocalQuaternion);
        this.actuator.scale.copy(targetLocalScale);
      } else {
        // 如果没有父级，直接设置世界坐标
        console.log('执行器无父级，直接设置世界坐标');
        this.actuator.position.copy(this.actuator.userData.initialWorldPosition);
        this.actuator.quaternion.copy(this.actuator.userData.initialWorldQuaternion);
        this.actuator.scale.copy(this.actuator.userData.initialWorldScale);
      }

      // 验证重置结果
      const newWorldPosition = new THREE.Vector3();
      const newWorldQuaternion = new THREE.Quaternion();
      const newWorldScale = new THREE.Vector3();
      this.actuator.getWorldPosition(newWorldPosition);
      this.actuator.getWorldQuaternion(newWorldQuaternion);
      this.actuator.getWorldScale(newWorldScale);
      console.log('世界坐标重置后位置:', newWorldPosition);
      console.log('世界坐标重置后旋转:', newWorldQuaternion);
      console.log('世界坐标重置后缩放:', newWorldScale);
      const worldPositionMatch = newWorldPosition.distanceTo(this.actuator.userData.initialWorldPosition) < 0.001;
      console.log('世界坐标重置是否成功:', worldPositionMatch);
      console.log('=== 世界坐标重置完成 ===');
    },
    // 设置相机控制为执行器查看模式
    setCameraControlsForActuatorView() {
      // 允许360度旋转和平移
      this.controls.enableRotate = true;
      this.controls.enablePan = true;
      this.controls.enableZoom = true;

      // 设置旋转限制，适合平视执行器
      // 限制垂直角度，避免过度俯视或仰视，保持平视效果
      this.controls.minPolarAngle = Math.PI / 6; // 30度，限制向下旋转
      this.controls.maxPolarAngle = Math.PI * 5 / 6; // 150度，限制向上旋转
      this.controls.minAzimuthAngle = -Infinity;
      this.controls.maxAzimuthAngle = Infinity;

      // 设置距离限制，防止过度缩放
      this.controls.minDistance = 0.5; // 最小距离
      this.controls.maxDistance = 20.0; // 最大距离

      // 设置阻尼效果
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
    },
    // 设置相机控制为正常模式
    setCameraControlsForNormalMode() {
      // 正常模式：允许旋转和缩放，但不允许平移
      this.controls.enableRotate = true;
      this.controls.enablePan = false;
      this.controls.enableZoom = true;

      // 设置旋转限制
      this.controls.minPolarAngle = 0;
      this.controls.maxPolarAngle = Math.PI;
      this.controls.minAzimuthAngle = -Infinity;
      this.controls.maxAzimuthAngle = Infinity;

      // 设置阻尼效果
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
    },
    // 隐藏所有模型（除了执行器）
    hideAllModelsExceptActuator() {
      // 隐藏天然气井道模型
      if (this.gasWellModel) {
        this.gasWellModel.visible = false;
      }

      // 隐藏所有天然气井道组件
      const modelsToHide = [{
        model: this.ground,
        name: '大地'
      }, {
        model: this.wellEdge,
        name: '井盖边缘'
      }, {
        model: this.wellBarrel,
        name: '井桶'
      }, {
        model: this.wellBackWall,
        name: '井桶后壁'
      }, {
        model: this.wellCover,
        name: '井盖'
      }, {
        model: this.ground2,
        name: '地层2'
      }, {
        model: this.wellGround,
        name: '井内土层'
      }, {
        model: this.bigGround,
        name: '土层-大'
      }, {
        model: this.pipeline,
        name: '管道'
      }];
      modelsToHide.forEach(({
        model,
        name
      }) => {
        if (model) {
          model.visible = false;
          console.log(`${name}模型已隐藏`);
        }
      });

      // 确保执行器模型可见
      if (this.actuator) {
        this.actuator.visible = true;
        console.log('✅ 执行器模型保持可见');

        // 确保执行器的所有子对象也可见
        this.actuator.traverse(child => {
          if (child.isMesh || child.isGroup) {
            child.visible = true;
            console.log(`执行器子对象 ${child.name} 设置为可见`);
          }
        });
      } else {}
    },
    // 清理资源
    cleanup() {
      // 移除事件监听器
      window.removeEventListener('resize', this.onWindowResize);

      // 清理Three.js对象
      if (this.renderer) {
        this.renderer.dispose();
        if (this.$refs.threeContainer && this.renderer.domElement) {
          this.$refs.threeContainer.removeChild(this.renderer.domElement);
        }
      }

      // 清理场景
      if (this.scene) {
        this.scene.clear();
      }
    }
  }
};